import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  ChevronDown,
  Copy,
  LogOut,
  Mail,
  MessageCircle,
  Monitor,
  Phone,
  Search,
  UserCheck,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PushCard } from '../components/PushCard';
import { Avatar, TagPill } from '../components/ui';
import { errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth-context-value';
import { AvailabilitySwitch } from '../components/AvailabilitySwitch';
import { recordWhatsappClick } from '../lib/distribution';
import { daysSince, formatDate, whatsappLink } from '../lib/format';
import {
  businessSummary,
  getLead,
  listBusinesses,
  listPipelines,
  listStages,
  moveBusinesses,
} from '../lib/resources';
import type { Business, Stage } from '../types';

const PAGE = 30;
const PREF_KEY = 'crm.mobile.pipeline';

const readPref = () => {
  try {
    return window.localStorage.getItem(PREF_KEY);
  } catch {
    return null;
  }
};

/**
 * "Meus leads" — tela do celular (Mobile1). O vendedor escolhe a pipeline, vê os
 * negócios por etapa e pega o número (WhatsApp ou copiar). Vendedor vê só os
 * próprios; dono e administrador veem todos. Tocar no lead abre a ficha resumida,
 * onde também dá para mudar a etapa.
 */
export function MobilePage() {
  const { session, isAdmin, logout, can } = useAuth();
  const [pipelineId, setPipelineId] = useState<string | null>(readPref);
  const [stageId, setStageId] = useState<string | null>(null);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Business | null>(null);

  // Só os negócios do vendedor; dono/administrador veem a pipeline inteira.
  const filter = useMemo(() => {
    const params = new URLSearchParams({ status: 'in_process' });
    if (!isAdmin && session) params.set('attendant', session.user.id);
    if (search) params.set('search', search);
    return params.toString();
  }, [isAdmin, session, search]);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchDraft.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [searchDraft]);

  const pipelines = useQuery({ queryKey: ['mobile', 'pipelines'], queryFn: listPipelines });
  const list = pipelines.data?.data ?? [];
  const current = list.find((item) => item.id === pipelineId) ?? list[0] ?? null;

  useEffect(() => {
    if (!current) return;
    try {
      window.localStorage.setItem(PREF_KEY, current.id);
    } catch {
      // navegador sem armazenamento: escolhe de novo na próxima vez
    }
  }, [current]);

  const stages = useQuery({
    queryKey: ['mobile', 'stages', current?.id],
    queryFn: () => listStages(current!.id),
    enabled: Boolean(current),
  });
  const summary = useQuery({
    queryKey: ['mobile', 'summary', current?.id, filter],
    queryFn: () => businessSummary(current!.id, filter),
    enabled: Boolean(current),
  });
  const counts = new Map(
    (summary.data ?? []).map((item) => [item.stageId, item.businessesCount]),
  );
  const stageList = [...(stages.data ?? [])].sort((a, b) => a.index - b.index);
  // Etapa escolhida, ou a primeira que tem negócio.
  const activeStage =
    stageList.find((stage) => stage.id === stageId) ??
    stageList.find((stage) => (counts.get(stage.id) ?? 0) > 0) ??
    stageList[0] ??
    null;

  const businesses = useInfiniteQuery({
    queryKey: ['mobile', 'businesses', current?.id, activeStage?.id, filter],
    enabled: Boolean(current && activeStage),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      listBusinesses(
        {
          pipelineId: current!.id,
          stageId: activeStage!.id,
          skip: pageParam,
          take: PAGE,
          sort: 'recent',
        },
        filter,
      ),
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.data.length, 0);
      return loaded < last.count ? loaded : undefined;
    },
  });
  const cards = (businesses.data?.pages ?? []).flatMap((page) => page.data);

  // Toque na notificação do celular: /m?lead=<id> abre a ficha do negócio em aberto dele.
  const [params, setParams] = useSearchParams();
  const fromPush = params.get('lead');
  const pushed = useQuery({
    queryKey: ['mobile', 'from-push', fromPush],
    queryFn: () => listBusinesses({ leadId: fromPush!, status: 'in_process', take: 1 }),
    enabled: Boolean(fromPush),
  });
  const sheet = open ?? (fromPush ? (pushed.data?.data[0] ?? null) : null);
  const closeSheet = () => {
    setOpen(null);
    if (fromPush) setParams({}, { replace: true });
  };

  return (
    <div
      className="flex min-h-screen flex-col bg-slate-50 text-slate-900"
      style={{ fontSize: 15 }}
    >
      <header
        className="sticky top-0 z-20 space-y-2.5 border-b border-slate-200 bg-white px-4 pb-3"
        style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-[11px] uppercase tracking-wide text-slate-400">
              {session?.workspace.name}
            </p>
            <h1 className="text-lg font-semibold">Meus leads</h1>
          </div>
          <div className="flex items-center gap-1">
            {can('pipeline_admin') && (
              <Link
                to="/m/equipe"
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
                title="Equipe: quem está disponível e a fila de leads"
                aria-label="Equipe"
              >
                <UserCheck size={18} />
              </Link>
            )}
            <Link
              to="/pipelines"
              className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
              title="Abrir o CRM completo"
              aria-label="Abrir o CRM completo"
            >
              <Monitor size={18} />
            </Link>
            <button
              type="button"
              onClick={logout}
              className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
              title="Sair"
              aria-label="Sair"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
          <AvailabilitySwitch tone="light" />
        </div>

        <label className="relative block">
          <select
            className="w-full appearance-none rounded-xl border border-slate-300 bg-white py-2.5 pl-3 pr-9 font-medium outline-none"
            value={current?.id ?? ''}
            onChange={(event) => {
              setPipelineId(event.target.value);
              setStageId(null);
            }}
          >
            {list.map((pipeline) => (
              <option key={pipeline.id} value={pipeline.id} className="bg-white text-slate-900">
                {pipeline.name}
              </option>
            ))}
          </select>
          <ChevronDown
            size={18}
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
        </label>

        <label className="relative block">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="search"
            className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-3 outline-none"
            placeholder="Buscar nome ou telefone"
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
          />
        </label>

        <nav className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-0.5" aria-label="Etapas">
          {stageList.map((stage) => {
            const active = stage.id === activeStage?.id;
            return (
              <button
                key={stage.id}
                type="button"
                onClick={() => setStageId(stage.id)}
                className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] ${
                  active
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-300 bg-white text-slate-600'
                }`}
              >
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: stage.color ?? '#94a3b8' }}
                />
                {stage.name}
                <span className={active ? 'opacity-80' : 'text-slate-400'}>
                  {counts.get(stage.id) ?? 0}
                </span>
              </button>
            );
          })}
        </nav>
      </header>

      <main
        className="flex-1 space-y-2.5 p-3"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}
      >
        <PushCard compact />
        {pipelines.isLoading || businesses.isLoading ? (
          <p className="py-10 text-center text-sm text-slate-400">Carregando…</p>
        ) : pipelines.error || businesses.error ? (
          <p className="rounded-xl bg-red-50 p-3 text-sm text-red-700">
            {errorMessage(pipelines.error ?? businesses.error)}
          </p>
        ) : !current ? (
          <p className="py-10 text-center text-sm text-slate-500">
            Nenhuma pipeline para você.
          </p>
        ) : cards.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-500">
            {search ? 'Nenhum lead com essa busca nesta etapa.' : 'Nenhum lead nesta etapa.'}
          </p>
        ) : (
          cards.map((business) => (
            <LeadCard key={business.id} business={business} onOpen={() => setOpen(business)} />
          ))
        )}
        {businesses.hasNextPage && (
          <button
            type="button"
            onClick={() => businesses.fetchNextPage()}
            disabled={businesses.isFetchingNextPage}
            className="w-full rounded-xl border border-slate-300 bg-white py-3 text-sm font-medium text-slate-600"
          >
            {businesses.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
          </button>
        )}
      </main>

      {sheet && <LeadSheet business={sheet} stages={stageList} onClose={closeSheet} />}
    </div>
  );
}

/** Copiar o número, com o "copiado" por 2 s. */
function useCopy() {
  const [copied, setCopied] = useState(false);
  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // navegador antigo: seleciona num campo escondido
      const input = document.createElement('textarea');
      input.value = value;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      input.remove();
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };
  return { copied, copy };
}

function PhoneActions({
  phone,
  rawPhone,
  leadId,
}: {
  phone: string | null;
  rawPhone: string | null;
  leadId: string;
}) {
  const { copied, copy } = useCopy();
  const wa = whatsappLink(rawPhone);
  if (!phone && !rawPhone) return <p className="text-sm text-slate-400">Sem telefone</p>;
  return (
    <div className="grid grid-cols-2 gap-2">
      {wa ? (
        <a
          href={wa}
          target="_blank"
          rel="noreferrer"
          onClick={(event) => {
            event.stopPropagation();
            recordWhatsappClick(leadId);
          }}
          className="flex items-center justify-center gap-1.5 rounded-xl bg-emerald-600 py-2.5 text-sm font-semibold text-white"
        >
          <MessageCircle size={17} /> WhatsApp
        </a>
      ) : (
        <span />
      )}
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          void copy(rawPhone ?? phone ?? '');
        }}
        className="flex items-center justify-center gap-1.5 rounded-xl border border-slate-300 bg-white py-2.5 text-sm font-semibold text-slate-700"
      >
        {copied ? <Check size={17} className="text-emerald-600" /> : <Copy size={17} />}
        {copied ? 'Copiado' : 'Copiar número'}
      </button>
    </div>
  );
}

function LeadCard({ business, onOpen }: { business: Business; onOpen: () => void }) {
  const lead = business.lead;
  const name = lead?.name ?? 'Lead';
  return (
    <article
      onClick={onOpen}
      className="space-y-3 rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm active:bg-slate-50"
    >
      <div className="flex items-start gap-3">
        <Avatar name={name} />
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold">{name}</p>
          <p className="flex items-center gap-1.5 text-[13px] text-slate-500">
            <Phone size={13} className="shrink-0" />
            <span className="truncate">{lead?.phone ?? 'Sem telefone'}</span>
          </p>
        </div>
        <span className="shrink-0 text-xs text-slate-400">
          {daysSince(business.lastMovedAt ?? business.createdAt)}d
        </span>
      </div>
      {(lead?.tags ?? []).length > 0 && (
        <div className="flex flex-wrap gap-1">
          {lead?.tags.slice(0, 3).map((tag) => (
            <TagPill key={tag.id} tag={tag} />
          ))}
          {(lead?.tags.length ?? 0) > 3 && (
            <span className="text-xs text-slate-400">+{(lead?.tags.length ?? 0) - 3}</span>
          )}
        </div>
      )}
      <PhoneActions
        phone={lead?.phone ?? null}
        rawPhone={lead?.rawPhone ?? null}
        leadId={business.leadId}
      />
    </article>
  );
}

/** Ficha resumida em gaveta de baixo: contato, campos, nota e mudar de etapa. */
function LeadSheet({
  business,
  stages,
  onClose,
}: {
  business: Business;
  stages: Stage[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const lead = useQuery({
    queryKey: ['lead', business.leadId],
    queryFn: () => getLead(business.leadId),
  });
  const [target, setTarget] = useState(business.stageId);
  const move = useMutation({
    mutationFn: () => moveBusinesses([business.id], target),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['mobile'] });
      onClose();
    },
  });
  const data = lead.data;
  const name = data?.name ?? business.lead?.name ?? 'Lead';
  const fields = (data?.additionalFields ?? []).filter((field) => field.value);

  return (
    <div
      className="fixed inset-0 z-40 flex items-end bg-black/40"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        className="max-h-[88vh] w-full space-y-4 overflow-y-auto rounded-t-3xl bg-white p-4"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}
      >
        <div className="flex items-start gap-3">
          <Avatar name={name} size="lg" />
          <div className="min-w-0 flex-1">
            <p className="text-lg font-semibold leading-tight">{name}</p>
            <p className="text-[13px] text-slate-500">
              Negócio #{business.code} · desde {formatDate(business.createdAt)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100"
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        <PhoneActions
          phone={data?.phone ?? business.lead?.phone ?? null}
          rawPhone={data?.rawPhone ?? business.lead?.rawPhone ?? null}
          leadId={business.leadId}
        />
        {data?.email && (
          <p className="flex items-center gap-2 text-sm text-slate-600">
            <Mail size={15} className="text-slate-400" /> {data.email}
          </p>
        )}

        <label className="block space-y-1">
          <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
            Etapa
          </span>
          <div className="flex gap-2">
            <select
              className="min-w-0 flex-1 rounded-xl border border-slate-300 bg-white px-3 py-2.5 outline-none"
              value={target}
              onChange={(event) => setTarget(event.target.value)}
            >
              {stages.map((stage) => (
                <option key={stage.id} value={stage.id} className="bg-white text-slate-900">
                  {stage.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={target === business.stageId || move.isPending}
              onClick={() => move.mutate()}
              className="rounded-xl px-4 text-sm font-semibold text-white disabled:opacity-40"
              style={{
                backgroundColor: 'var(--crm-primary)',
                color: 'var(--crm-primary-foreground)',
              }}
            >
              {move.isPending ? 'Movendo…' : 'Mover'}
            </button>
          </div>
          {move.error && <p className="text-sm text-red-600">{errorMessage(move.error)}</p>}
        </label>

        {lead.isLoading && <p className="text-sm text-slate-400">Carregando ficha…</p>}

        {fields.length > 0 && (
          <dl className="grid grid-cols-2 gap-x-3 gap-y-2 rounded-xl bg-slate-50 p-3 text-sm">
            {fields.map((field) => (
              <div key={field.id} className="min-w-0">
                <dt className="truncate text-[11px] uppercase tracking-wide text-slate-400">
                  {field.name}
                </dt>
                <dd className="truncate">{field.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {data?.notes && (
          <div className="space-y-1">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
              Nota
            </p>
            <p className="whitespace-pre-wrap rounded-xl bg-slate-50 p-3 text-sm">
              {data.notes}
            </p>
          </div>
        )}

        {(data?.tags ?? []).length > 0 && (
          <div className="flex flex-wrap gap-1">
            {data?.tags.map((tag) => (
              <TagPill key={tag.id} tag={tag} />
            ))}
          </div>
        )}

        <Link
          to={`/negocios/${business.id}`}
          className="block text-center text-sm text-slate-500 underline"
        >
          Abrir no CRM completo
        </Link>
      </section>
    </div>
  );
}
